import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import './UpdateStatus.css';

interface UpdateStatusData {
  lastGlobalUpdate: string | null;
  totalClients: number;
  clientsWithRecentData: number;
  updatePercentage: number;
}

const UpdateStatus: React.FC = () => {
  const [updateStatus, setUpdateStatus] = useState<UpdateStatusData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUpdateStatus = async () => {
    try {
      const response = await fetch('/api/clients/update-status');
      if (response.ok) {
        const result = await response.json();
        setUpdateStatus(result.data);
      }
    } catch (error) {
      console.error('Error fetching update status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdateStatus();
    // Actualizar cada 5 minutos
    const interval = setInterval(fetchUpdateStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatLastUpdate = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    const now = new Date();
    
    // Convertir ambas fechas a zona horaria de Colombia
    const nowInColombia = new Date(now.toLocaleString('en-US', {timeZone: 'America/Bogota'}));
    const dateInColombia = new Date(date.toLocaleString('en-US', {timeZone: 'America/Bogota'}));
    
    const diffMs = nowInColombia.getTime() - dateInColombia.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'Hace menos de 1 minuto';
    if (diffMinutes < 60) return `Hace ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    
    return date.toLocaleDateString('es-CO', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = () => {
    if (loading) return <RefreshCw size={16} className="spinning" />;
    if (!updateStatus?.lastGlobalUpdate) return <AlertCircle size={16} />;
    
    const lastUpdate = new Date(updateStatus.lastGlobalUpdate);
    const now = new Date();
    
    // Convertir ambas fechas a zona horaria de Colombia
    const nowInColombia = new Date(now.toLocaleString('en-US', {timeZone: 'America/Bogota'}));
    const updateInColombia = new Date(lastUpdate.toLocaleString('en-US', {timeZone: 'America/Bogota'}));
    const diffHours = (nowInColombia.getTime() - updateInColombia.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 2) return <CheckCircle size={16} />;
    if (diffHours < 12) return <Clock size={16} />;
    return <AlertCircle size={16} />;
  };

  const getStatusClass = () => {
    if (loading) return 'loading';
    if (!updateStatus?.lastGlobalUpdate) return 'error';
    
    const lastUpdate = new Date(updateStatus.lastGlobalUpdate);
    const now = new Date();
    
    // Convertir ambas fechas a zona horaria de Colombia
    const nowInColombia = new Date(now.toLocaleString('en-US', {timeZone: 'America/Bogota'}));
    const updateInColombia = new Date(lastUpdate.toLocaleString('en-US', {timeZone: 'America/Bogota'}));
    const diffHours = (nowInColombia.getTime() - updateInColombia.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 2) return 'success';
    if (diffHours < 12) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <div className="update-status loading">
        <RefreshCw size={16} className="spinning" />
        <span>Cargando estado de actualización...</span>
      </div>
    );
  }

  return (
    <div className={`update-status ${getStatusClass()}`}>
      <div className="status-icon">
        {getStatusIcon()}
      </div>
      <div className="status-content">
        <div className="status-main">
          <span className="status-label">Última actualización:</span>
          <span className="status-time">{formatLastUpdate(updateStatus?.lastGlobalUpdate || null)}</span>
        </div>
        {updateStatus && (
          <div className="status-details">
            <span className="clients-updated">
              {updateStatus.clientsWithRecentData} de {updateStatus.totalClients} clientes actualizados ({updateStatus.updatePercentage}%)
            </span>
          </div>
        )}
      </div>
      <button 
        className="refresh-button" 
        onClick={fetchUpdateStatus}
        title="Actualizar estado"
      >
        <RefreshCw size={14} />
      </button>
    </div>
  );
};

export default UpdateStatus;
